import { Analytics } from '@util/Analytics';
import React, { ReactElement } from 'react';
import styled from 'styled-components';
import { isDefined } from 'ts-utils';
import { Color } from '../../config/Color';
import { LayoutScreenConfig, ScreenType } from '../../config/FlowTypes';
import { AppSelector, useAppStore } from '../../store/Store';
import { openUrlInNewTab } from '../../util/FlowUtils';
import { ScreenWrapper } from '../styles/Containers';
import { NavigationProps } from './FlowNavigation';
import {
  PresentationContent,
  PresentationLayout,
  PresentationLayoutConfig,
} from './layouts/PresentationLayout';

// Because we're not exposing the PresentationLayoutConfig directly we have a separate layout config
type ExternalLinkLayoutConfig = Omit<PresentationLayoutConfig, 'content'> & {
  /** Accepts function for differing external link creation to when the screen is viewed. */
  externalUrl: string | (() => string);
  /**
   * Screen supports has a `usersEmailContainer` content type, this is the users email within a grey container.
   * it is only rendered if screen is presented after the auth screen.
   */
  content: (PresentationContent | { type: 'usersEmailContainer' })[];
};

export type ExternalLinkScreenConfig<ScreenNames extends string> = LayoutScreenConfig<
  ScreenType.ExternalLink,
  ExternalLinkLayoutConfig,
  ScreenNames
>;

type ExternalLinkScreenProps<ScreenNames extends string> = NavigationProps<
  ExternalLinkScreenConfig<ScreenNames>,
  ScreenNames
>;

export const ExternalLinkScreen = <ScreenNames extends string>(
  props: ExternalLinkScreenProps<ScreenNames>
): ReactElement => {
  const { layout } = props;
  const { externalUrl, primaryButtonTitle, content, ...restLayout } = layout;

  const parsedExternalUrl = typeof externalUrl === 'string' ? externalUrl : externalUrl();

  function handlePrimaryButtonPress() {
    Analytics.track({
      event: Analytics.Event.o_external_url_opened,
      screen: props.screenName,
      externalUrl: parsedExternalUrl,
    });

    openUrlInNewTab(parsedExternalUrl);
  }

  return (
    <ScreenWrapper>
      <PresentationLayout
        onPressPrimaryButton={handlePrimaryButtonPress}
        primaryButtonTitle={primaryButtonTitle ?? 'Get more info'}
        content={content.map((item) =>
          item.type === 'usersEmailContainer'
            ? { type: 'custom', content: <UsersEmailContainer /> }
            : item
        )}
        {...restLayout}
      />
    </ScreenWrapper>
  );
};

const UsersEmailContainer = () => {
  const { user } = useAppStore((state) => ({ user: AppSelector.user(state) }));
  return isDefined(user.email) ? <StyledEmailContainer>{user.email}</StyledEmailContainer> : null;
};

const StyledEmailContainer = styled.div`
  background-color: ${Color.flow.muted};
  border-radius: 60px;
  width: fit-content;
  max-width: 310px;
  overflow-wrap: break-word;
  padding: ${({ theme }) => `${theme.spacing._16px} ${theme.spacing._26px}`};
  margin: ${({ theme }) => `${theme.spacing._16px} auto`};
  text-align: center;
`;
